import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { Card } from '../components/ui/Card.js';
import { Pill } from '../components/PillGroup.js';
import { Button } from '../components/ui/Button.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Icon, type IconName } from '../components/Icon.js';
import { OnboardingClaudePanel } from '../components/onboarding/OnboardingClaudePanel.js';
import { ContactImportWizard } from '../components/contacts/ContactImportWizard.js';
import { TrialNotice } from '../components/subscription/TrialNotice.js';
import { WhatsappConnect } from '../components/whatsapp/WhatsappConnect.js';
import { useToast } from '../components/ui/Toast.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { useSubscription, useCreateSubscriptionCheckout, useAvailablePlans } from '../hooks/useSettings.js';
import { ImapSection, CompanyProfileForm } from './SettingsPage.js';
import {
  useOnboardingProgress,
  useComposioConnections,
  useStartComposioConnection,
  useConnectionIdentities,
  useDisconnectToolkit,
  type OnboardingStep,
  type OnboardingStepStatus,
  type ConnectableToolkit,
  useOnboardingRefreshOnFocus,
} from '../hooks/useOnboarding.js';

const STEP_ORDER: OnboardingStep[] = ['business_description', 'email', 'connections', 'contacts', 'billing', 'tour', 'activation'];
const CONNECTABLE: ConnectableToolkit[] = ['instagram', 'facebook', 'googlecalendar'];
const CONNECTION_ICON: Partial<Record<ConnectableToolkit, IconName>> = {
  instagram: 'social-instagram',
  facebook: 'social-facebook',
};

function statusTone(status: OnboardingStepStatus): 'success' | 'muted' | 'warn' {
  if (status === 'completed') return 'success';
  if (status === 'skipped') return 'warn';
  return 'muted';
}

function StepStatusPill({ status }: { status: OnboardingStepStatus }) {
  const { t } = useTranslation();
  return (
    <Pill size="sm" variant="subtle" tone={statusTone(status)}>
      {t(`onboarding.status.${status}`)}
    </Pill>
  );
}

function ConnectionRow({ toolkit }: { toolkit: ConnectableToolkit }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data } = useComposioConnections();
  const startConnection = useStartComposioConnection();
  const status = data?.statuses.find(s => s.toolkit === toolkit);
  const connected = status?.connected ?? false;
  const intentoVencido = !!status?.intentoVencidoAt;
  const icon = CONNECTION_ICON[toolkit];
  // Minutos que quedan del link recién abierto. null = todavía no se abrió
  // ninguno en esta visita.
  const [ventanaMin, setVentanaMin] = useState<number | null>(null);
  // Solo se pregunta por identidades si hay algo conectado: sin conexiones, la
  // llamada no puede devolver nada útil.
  const { data: identidades } = useConnectionIdentities(connected);
  const identidad = identidades?.identities.find(i => i.toolkit === toolkit)?.identidad ?? null;
  const desconectar = useDisconnectToolkit();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  async function handleDisconnect() {
    const ok = await confirmAction({
      title: t('onboarding.connections.disconnectConfirm', {
        service: t(`onboarding.connections.${toolkit}`),
        account: identidad ?? t('onboarding.connections.unknownAccount'),
      }),
      tone: 'danger',
    });
    if (!ok) return;
    desconectar.mutate(toolkit, {
      onError: err => toast.error(err instanceof Error ? err.message : String(err)),
    });
  }

  async function handleConnect() {
    try {
      const link = await startConnection.mutateAsync(toolkit);
      // El link de Composio vence a los 10 minutos de creado. Se guarda cuánto
      // queda para decirlo en pantalla: si se pasa, el cliente termina en una
      // página en inglés de Composio ("Link session has expired") sin camino de
      // vuelta. Le pasó al cliente de Villa Sión tres veces con Instagram.
      const minutos = Math.max(1, Math.round((new Date(link.expiresAt).getTime() - Date.now()) / 60_000));
      setVentanaMin(Number.isFinite(minutos) ? minutos : 10);
      window.open(link.redirectUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // El backend ya le avisó a Eduardo (composioConnections.ts) — acá solo
      // se muestra el estado a través del pill de error, sin bloquear el resto.
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-3)' }}>
      {confirmDialog}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', minWidth: 0 }}>
        {icon && <Icon name={icon} size={16} />}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)' }}>
            {t(`onboarding.connections.${toolkit}`)}
          </span>
          {/* De QUIÉN es la cuenta autorizada. Sin esto, autorizar con la
              cuenta equivocada se ve idéntico a hacerlo bien, y el error
              aparece una semana después cuando no llega ningún mensaje. */}
          {connected && (
            <span style={{
              fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {identidad ?? t('onboarding.connections.unknownAccount')}
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)' }}>
        <Pill size="sm" variant="subtle" tone={connected ? 'success' : 'muted'}>
          {connected ? t('onboarding.connections.connected') : t('onboarding.connections.notConnected')}
        </Pill>
        <Button
          variant="outline"
          tone="cyan"
          size="sm"
          loading={startConnection.isPending}
          onClick={() => { void handleConnect(); }}
        >
          {connected ? t('onboarding.connections.reconnectBtn') : t('onboarding.connections.connectBtn')}
        </Button>
        {/* Se puede conectar mal desde el producto; tiene que poder arreglarse
            desde el producto. */}
        {connected && (
          <Button
            variant="ghost" tone="danger" size="sm"
            loading={desconectar.isPending}
            onClick={() => { void handleDisconnect(); }}
          >
            {t('onboarding.connections.disconnectBtn')}
          </Button>
        )}
      </div>
      {startConnection.isError && (
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-danger)' }}>
          {t('onboarding.connections.connectError')}
        </span>
      )}
    </div>

      {/* Lo que faltaba: decir que hay reloj, decir que el intento anterior se
          venció, y decir qué pide Instagram ANTES de salir a autorizar. Sin
          esto, el cliente vuelve a hacer clic sin saber nada de lo tres. */}
      {!connected && ventanaMin !== null && (
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>
          {t('onboarding.connections.ventana', { minutos: ventanaMin })}
        </span>
      )}
      {!connected && ventanaMin === null && intentoVencido && (
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warning)', lineHeight: 1.5 }}>
          {t('onboarding.connections.intentoVencido')}
        </span>
      )}
      {!connected && toolkit === 'instagram' && (
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>
          {t('onboarding.connections.instagramRequisito')}
        </span>
      )}
    </div>
  );
}

/** Paso 4 — cargar la lista de contactos. Reusa el ContactImportWizard del
 *  módulo de Contactos tal cual: es el mismo trabajo (CSV/Excel → contactos) y
 *  un importador propio de la inducción sería un segundo camino que mantener,
 *  con sus propias diferencias de mapeo y validación. */
function ContactsStepBody() {
  const { t } = useTranslation();
  const [showImport, setShowImport] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', margin: 0, lineHeight: 1.6 }}>
        {t('onboarding.step.contacts.description')}
      </p>
      <div>
        <Button variant="solid" tone="cyan" size="sm" onClick={() => setShowImport(true)}>
          {t('onboarding.step.contacts.importBtn', 'Cargar lista de contactos')}
        </Button>
      </div>
      {showImport && <ContactImportWizard onClose={() => setShowImport(false)} />}
    </div>
  );
}

/** Paso 7 — activar la cuenta. Último a propósito: sin tarjeta al inicio, el
 *  cliente recorre los seis pasos y ve la herramienta andando antes de que se
 *  le pida la plata. Antes la inducción terminaba en nada. */
function ActivationStepBody() {
  const { t } = useTranslation();
  const { data } = useSubscription();
  const { data: plansData, isLoading: plansLoading } = useAvailablePlans();
  const plans = plansData?.plans ?? [];
  const checkout = useCreateSubscriptionCheckout();
  const toast = useToast();

  function goToCheckout(planId?: string) {
    checkout.mutate(planId, {
      onSuccess: r => { window.location.href = r.url; },
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)),
    });
  }

  if (data?.status === 'active') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
        <span style={{ color: 'var(--evo-success)', display: 'inline-flex' }}>
          <Icon name="ok" size={16} />
        </span>
        <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)' }}>
          {t('onboarding.step.activation.done', 'Tu suscripción está activa.')}
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', margin: 0, lineHeight: 1.6 }}>
        {t('onboarding.step.activation.description')}
      </p>

      {/* El mismo aviso de prueba de la banda superior, acá en bloque: es el
          lugar donde el cliente está decidiendo, así que el contador y el
          botón tienen que estar juntos. */}
      <TrialNotice variant="block" />

      {/* Tres casos, y ninguno termina en un botón muerto:
            - precio de excepción (data.priceId) → ese plan solo, sin elección
            - catálogo con planes                → el cliente elige
            - catálogo vacío                     → se dice por qué no puede pagar
          Un botón gris que no responde al clic deja al cliente adivinando, que
          es exactamente lo que pasó con villa-sion el 2026-08-18. */}
      {!data?.priceId && plans.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
          {plans.map(plan => (
            <div key={plan.id} style={{
              display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)',
              border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)',
              padding: 'var(--evo-space-4)', background: 'var(--evo-bg-2)',
            }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)' }}>{plan.name}</span>
                {plan.description && (
                  <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>{plan.description}</span>
                )}
              </div>
              {/* El precio ANTES del botón: enterarse en la pantalla de Stripe
                  genera una llamada. Viene de Stripe, no de nuestra DB. */}
              <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)' }}>
                {plan.amount != null
                  ? `${(plan.amount / 100).toLocaleString(undefined, { style: 'currency', currency: (plan.currency ?? 'usd').toUpperCase() })}${plan.interval ? ` / ${t(`settings.subscription.interval.${plan.interval}`, plan.interval)}` : ''}`
                  : '—'}
              </span>
              <Button
                variant="solid" tone="cyan" size="sm"
                loading={checkout.isPending}
                onClick={() => goToCheckout(plan.id)}
              >
                {t('onboarding.step.activation.chooseBtn', 'Elegir')}
              </Button>
            </div>
          ))}
        </div>
      )}

      {!data?.priceId && !plansLoading && plans.length === 0 && (
        <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>
          {t('onboarding.step.activation.noPlan')}
        </span>
      )}

      {data?.priceId ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)' }}>
          {/* El precio ANTES del botón, mismo criterio que Ajustes: enterarse
              en la pantalla de Stripe genera una llamada. */}
          <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)' }}>
            {data.priceAmount != null
              ? `${(data.priceAmount / 100).toLocaleString(undefined, { style: 'currency', currency: (data.priceCurrency ?? 'usd').toUpperCase() })} / ${t(`settings.subscription.interval.${data.priceInterval ?? 'month'}`, 'mes')}`
              : '—'}
          </span>
          <Button variant="solid" tone="cyan" size="sm" loading={checkout.isPending} onClick={() => goToCheckout()}>
            {t('onboarding.step.activation.activateBtn', 'Activar suscripción')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * El paso `tour` de la inducción.
 *
 * Hasta hoy decía "cuando quieras, pídele a Evo Brain un recorrido" y no hacía
 * nada — un paso de una lista de tareas sin tarea. Ahora lleva al módulo y el
 * recorrido sale ahí, sobre la pantalla real y con los datos de esa cuenta,
 * que es donde significa algo.
 *
 * Va a Contactos porque es el módulo que toda cuenta tiene, tenga o no el resto
 * encendido.
 */
function TourStepBody() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', margin: 0, lineHeight: 1.6 }}>
        {t('onboarding.step.tour.description')}
      </p>
      <div>
        <Button
          variant="solid"
          tone="cyan"
          onClick={() => {
            // Se borra la marca de "ya salió uno en esta sesión": pedirlo es
            // una decisión, y el tope de uno por sesión existe para no
            // avasallar a quien NO lo pidió.
            try { sessionStorage.removeItem('evo-recorrido-visto-en-esta-sesion'); } catch { /* sin almacenamiento */ }
            navigate('/contacts?recorrido=1');
          }}
        >
          {t('recorrido.volverAVer')}
        </Button>
      </div>
    </div>
  );
}

function StepBody({ step }: { step: OnboardingStep }) {
  const { t } = useTranslation();
  if (step === 'email') return <ImapSection />;
  if (step === 'tour') return <TourStepBody />;
  if (step === 'billing') return <CompanyProfileForm />;
  if (step === 'contacts') return <ContactsStepBody />;
  if (step === 'activation') return <ActivationStepBody />;
  if (step === 'connections') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
        <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', margin: 0, lineHeight: 1.6 }}>
          {t('onboarding.step.connections.description')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
          {CONNECTABLE.map(toolkit => <ConnectionRow key={toolkit} toolkit={toolkit} />)}
          {/* WhatsApp no va por Composio: el cliente es titular de su cuenta en
              360dialog y pega su clave. Por eso es un bloque propio y no otra
              ConnectionRow — el trabajo que tiene que hacer antes es distinto. */}
          <div style={{ borderTop: '1px solid var(--evo-stroke)', paddingTop: 'var(--evo-space-4)' }}>
            <WhatsappConnect />
          </div>
        </div>
      </div>
    );
  }
  return (
    <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', margin: 0, lineHeight: 1.6 }}>
      {t(`onboarding.step.${step}.description`)}
    </p>
  );
}

export function OnboardingPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useOnboardingProgress();
  const progress = data?.progress ?? [];
  const byStep = new Map(progress.map(s => [s.step, s]));
  const done = progress.filter(s => s.status === 'completed' || s.status === 'skipped').length;
  const { setOverride } = useTopBarCtx();
  // Refresco por foco: el caso real es volver de la pestaña de OAuth. Sustituye
  // al intervalo de 4s como mecanismo principal de detección.
  useOnboardingRefreshOnFocus();

  // El título va en la banda superior, como en el resto de la app. Antes
  // "Welcome to EvoHub" estaba adentro del contenido con la banda vacía
  // arriba — la primera pantalla del cliente se veía distinta a todas las demás.
  useEffect(() => {
    setOverride({
      title: t('onboarding.pageTitle'),
      subtitle: t('onboarding.pageSubtitle'),
      heroIcon: 'bolt',
      heroIconColor: 'var(--evo-cyan)',
    });
    return () => setOverride({});
  }, [t, setOverride]);

  return (
    <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
      <div
        className="animate-fade-blur-in"
        style={{
          flex: 1,
          minWidth: 0,
          overflowY: 'auto',
          padding: 'var(--evo-space-6) var(--evo-content-pad-x)',
        }}
      >
        <div style={{ maxWidth: '760px' }}>
          {progress.length > 0 && (
            <div style={{ marginBottom: 'var(--evo-space-4)' }}>
              <Pill size="sm" variant="subtle" tone="cyan">
                {t('onboarding.progressLabel', { done, total: progress.length })}
              </Pill>
            </div>
          )}

          {isLoading && <LoadingState />}

          {/* Una tarjeta por paso, no seis secciones apiladas: con inicio y
              fin visibles el cliente ve dónde está y cuánto falta. Es la
              primera pantalla que ve alguien que acaba de pagar. */}
          {!isLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
              {STEP_ORDER.map((step, idx) => {
                const status = byStep.get(step)?.status ?? 'pending';
                const isDone = status === 'completed' || status === 'skipped';
                return (
                  <Card
                    key={step}
                    padding="lg"
                    {...(isDone ? { style: { opacity: 0.72 } } : {})}
                    header={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)', width: '100%' }}>
                        {/* El número hace legible el avance de un vistazo:
                            "voy por el 3 de 6" sin tener que contar tarjetas. */}
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: '26px', height: '26px', borderRadius: 'var(--evo-r-pill)', flexShrink: 0,
                          fontSize: 'var(--evo-fs-caption)', fontWeight: 'var(--evo-fw-semibold)',
                          background: isDone ? 'var(--evo-cyan)' : 'var(--evo-bg-2)',
                          color: isDone ? '#0a0a0a' : 'var(--evo-fg-3)',
                          border: isDone ? 'none' : '1px solid var(--evo-stroke)',
                        }}>
                          {idx + 1}
                        </span>
                        <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-display)', color: 'var(--evo-fg-1)' }}>
                          {t(`onboarding.step.${step}.title`)}
                        </span>
                        <StepStatusPill status={status} />
                      </div>
                    }
                  >
                    <StepBody step={step} />
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <OnboardingClaudePanel />
    </div>
  );
}
